import { Link, Navigate } from '@tanstack/react-router';
import { PublicShell } from '../../public/PublicShell.js';
import { garageMonogram } from '../../shared/components/BrandMark.js';
import { useMe } from '../../shared/auth.js';
import { Avatar } from '../../ui/Avatar.js';
import { Badge } from '../../ui/Badge.js';
import { Button } from '../../ui/Button.js';
import { cx } from '../../ui/cx.js';
import { BuildingIcon, ChevronRightIcon } from '../../ui/icons.js';
import { Alert, SkeletonList } from '../../ui/States.js';
import { ROLE_LABELS } from '../lib/format.js';
import { garagePath } from '../lib/paths.js';
import { useLogout } from '../lib/session.js';

const ROW =
  'group flex min-h-16 w-full items-center gap-3 rounded-2xl border border-line bg-surface px-3.5 py-2.5 text-left transition-[border-color,background-color] duration-150 ease-out';
const ROW_LINK =
  'hover:border-line-strong hover:bg-hover active:bg-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus';

/**
 * Choix de l'espace de travail (vague 5) : les garages de l'utilisateur (adresse `/g/<slug>/`), un
 * garage suspendu grisé « Accès suspendu », et « Console plateforme » pour un administrateur.
 * Changer de garage passe par l'adresse : l'espace garage appelle `auth.switchGarage({ slug })`.
 */
export default function ChooseGaragePage() {
  const me = useMe();
  const logout = useLogout();

  if (me.isPending) {
    return (
      <PublicShell title="Choisir un garage">
        <SkeletonList rows={2} />
      </PublicShell>
    );
  }
  if (!me.data) return <Navigate to="/login" replace />;
  const { memberships, isPlatformAdmin, user, active } = me.data;
  const current = active?.garageSlug;

  return (
    <PublicShell
      title={isPlatformAdmin ? 'Choisir un espace' : 'Choisir un garage'}
      intro={`Bonjour ${user.name}. Où travaillez-vous ?`}
    >
      {memberships.length === 0 && !isPlatformAdmin ? (
        <Alert tone="warning">Votre compte n’est rattaché à aucun garage pour le moment.</Alert>
      ) : null}
      <ul className="space-y-2.5">
        {memberships.map((m) => {
          const suspended = m.status === 'suspended';
          const body = (
            <>
              <Avatar
                name={garageMonogram(m.garageName).split('').join(' ') || m.garageName}
                size="md"
                className="rounded-xl"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-base font-semibold tracking-[-0.01em] text-ink">{m.garageName}</span>
                <span className="block text-sm text-ink-3">{ROLE_LABELS[m.role]}</span>
              </span>
              {suspended ? (
                <Badge tone="warning">Accès suspendu</Badge>
              ) : m.garageSlug === current ? (
                <Badge tone="neutral">Actuel</Badge>
              ) : (
                <ChevronRightIcon
                  size={18}
                  className="text-ink-3 transition-transform duration-150 ease-out group-hover:translate-x-0.5"
                />
              )}
            </>
          );
          return (
            <li key={m.membershipId}>
              {suspended ? (
                <div aria-disabled="true" className={cx(ROW, 'bg-sunken shadow-none opacity-75')}>
                  {body}
                </div>
              ) : (
                <Link to={garagePath(m.garageSlug)} className={cx(ROW, ROW_LINK)}>
                  {body}
                </Link>
              )}
            </li>
          );
        })}
        {isPlatformAdmin ? (
          <li>
            <Link to="/admin" className={cx(ROW, ROW_LINK)}>
              <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-platform-panel text-on-platform">
                <BuildingIcon size={20} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-base font-semibold tracking-[-0.01em] text-ink">Console plateforme</span>
                <span className="block text-sm text-ink-3">Garages, accès support</span>
              </span>
              <ChevronRightIcon
                size={18}
                className="text-ink-3 transition-transform duration-150 ease-out group-hover:translate-x-0.5"
              />
            </Link>
          </li>
        ) : null}
      </ul>
      <div className="mt-8">
        <Button variant="quiet" block onClick={() => void logout()}>
          Se déconnecter
        </Button>
      </div>
    </PublicShell>
  );
}
